import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { clearZoneSession } from '../lib/zones';

/**
 * CogDropdown — settings cog button + dropdown menu for the canonical
 * staff-portal navbar (Phase 5d-1, Decision 5-26).
 *
 * Ports the `dropdownHTML()` structure from nexa-demo's `nexanomics-nav.js`
 * into React. Sections present at MVP — kept deliberately minimal per the
 * 5d-1 build brief "DO NOT add other Admin items":
 *
 *   Admin    — User Management / Customers / Partners / Networks / Nexa Config
 *   (Sign Out at the bottom)
 *
 * Account section omitted — Profile modal is not wired in portal-staff yet.
 *
 * Role visibility (5d-3b UAT Fix 1) — the COG is navigation only, so the
 * Admin section and every item in it is visible to ALL authenticated staff
 * roles (super_admin, nexa_admin, support, read_only, audit_only). Page-level
 * role gates (already in place on each destination route, backed by the
 * server-side ADR-012 gates) decide what each role can actually see or do.
 * Previously these items were gated to super_admin only, which left
 * nexa_admin / support / read_only unable to navigate to surfaces they are
 * permitted to view.
 */
export function CogDropdown() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Close on outside click + Escape — matches the canonical nav.js behaviour.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const partnersActive = pathname === '/admin/partners' || pathname.startsWith('/admin/partners/');
  const usersActive = pathname.startsWith('/admin/users');
  const betaInterestActive = pathname.startsWith('/admin/beta-interest');
  const customersActive = pathname.startsWith('/admin/customers');
  const networksActive = pathname.startsWith('/admin/networks');
  const monitoringActive = pathname.startsWith('/admin/monitoring');
  const growthActive = pathname.startsWith('/admin/growth');
  const gradingActive = pathname.startsWith('/admin/grading');
  const financeActive = pathname.startsWith('/admin/finance');
  const agentsActive = pathname.startsWith('/admin/agents');
  const configActive = pathname.startsWith('/admin/config');
  const diagsActive = pathname.startsWith('/admin/diags');
  const testBenchActive = pathname.startsWith('/admin/test-bench');
  const alertsActive = pathname.startsWith('/admin/alerts');
  const snippetTelemetryActive = pathname.startsWith('/admin/snippet-telemetry');
  const followUpActive = pathname.startsWith('/admin/launchpad-follow-up');
  const profileActive = pathname.startsWith('/admin/profile');

  const handlePartnersClick = () => {
    setOpen(false);
    navigate('/admin/partners');
  };
  const goTo = (path: string) => () => {
    setOpen(false);
    navigate(path);
  };

  const handleSignOut = () => {
    setOpen(false);
    // ADR-124 A3 — the next sign-in in this tab starts from the default zone and is told so again.
    clearZoneSession();
    void signOut();
  };

  return (
    <div className="portal-cog" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`portal-cog-btn${open ? ' open' : ''}`}
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <CogIcon />
      </button>
      {open && (
        <div className="portal-cog-dropdown" role="menu">
          {/* Admin section — navigation only; visible to all authenticated
              staff roles (5d-3b UAT Fix 1). Page-level + server-side ADR-012
              gates enforce what each role can read or mutate. */}
          <div className="portal-cog-group-label">Admin</div>
          <Link
            to="/admin/users"
            role="menuitem"
            className={`portal-cog-item${usersActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/users')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F465;</span>
            User Management
          </Link>
          <Link
            to="/admin/beta-interest"
            role="menuitem"
            className={`portal-cog-item${betaInterestActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/beta-interest')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F4E5;</span>
            Nexa Interest
          </Link>
          <Link
            to="/admin/customers"
            role="menuitem"
            className={`portal-cog-item${customersActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/customers')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F3E2;</span>
            Customers
          </Link>
          <Link
            to="/admin/partners"
            role="menuitem"
            className={`portal-cog-item${partnersActive ? ' active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handlePartnersClick();
            }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">
              &#x1F91D;
            </span>
            Partners
          </Link>
          <Link
            to="/admin/networks"
            role="menuitem"
            className={`portal-cog-item${networksActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/networks')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F578;</span>
            Networks
          </Link>
          <Link
            to="/admin/monitoring"
            role="menuitem"
            className={`portal-cog-item${monitoringActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/monitoring')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F4CA;</span>
            Monitoring
          </Link>
          {/* ADR-071 A7 — "Alerts" (deliberately not "Events"; Events would mean
              the raw emitEvent stream alerts are derived from). */}
          <Link
            to="/admin/alerts"
            role="menuitem"
            className={`portal-cog-item${alertsActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/alerts')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F514;</span>
            Alerts
          </Link>
          {/* ADR-075 — staff Cedar AAA: Growth / Finance business-role surfaces +
              Agents (ruleset management). Navigation only; the Cedar decision in
              admin-api governs what each role can actually read. */}
          <Link
            to="/admin/growth"
            role="menuitem"
            className={`portal-cog-item${growthActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/growth')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F4C8;</span>
            Growth
          </Link>
          <Link
            to="/admin/finance"
            role="menuitem"
            className={`portal-cog-item${financeActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/finance')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F4B0;</span>
            Finance
          </Link>
          <Link
            to="/admin/agents"
            role="menuitem"
            className={`portal-cog-item${agentsActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/agents')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F916;</span>
            Agents
          </Link>
          <Link
            to="/admin/config"
            role="menuitem"
            className={`portal-cog-item${configActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/config')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x2699;</span>
            Nexa Config
          </Link>
          <Link
            to="/admin/diags"
            role="menuitem"
            className={`portal-cog-item${diagsActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/diags')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F50D;</span>
            Diagnostics
          </Link>
          {/* The Test Bench (s3-grading-engine-node24) — test an upgraded copy of a function against the live one. */}
          <Link
            to="/admin/test-bench"
            role="menuitem"
            className={`portal-cog-item${testBenchActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/test-bench')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F9EA;</span>
            Test Bench
          </Link>
          {/* ADR-071 A3 — snippet telemetry surface (raw beacon drill-in). */}
          <Link
            to="/admin/snippet-telemetry"
            role="menuitem"
            className={`portal-cog-item${snippetTelemetryActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/snippet-telemetry')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F4E1;</span>
            Snippet Telemetry
          </Link>
          {/* ADR-127 D17 — the human follow-up list for snippets still not live after ~7 days. */}
          <Link
            to="/admin/launchpad-follow-up"
            role="menuitem"
            className={`portal-cog-item${followUpActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/launchpad-follow-up')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x260E;</span>
            Snippet follow-up
          </Link>
          {/* ADR-133 Amendment 2 §1 — the COMMUNITY section: Grading (community grading — graders, batches, quality, the
              queue rules, the workflow). Visible to every staff role; the page shows mutators only where Cedar allows. */}
          <div className="portal-cog-divider" />
          <div className="portal-cog-group-label">Community</div>
          <Link
            to="/admin/grading"
            role="menuitem"
            className={`portal-cog-item${gradingActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/grading')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F441;</span>
            Grading
          </Link>
          {/* telemetry-uat2 — My Profile (name, timezone, password). */}
          <div className="portal-cog-divider" />
          <div className="portal-cog-group-label">Account</div>
          <Link
            to="/admin/profile"
            role="menuitem"
            className={`portal-cog-item${profileActive ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); goTo('/admin/profile')(); }}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">&#x1F464;</span>
            My Profile
          </Link>
          <div className="portal-cog-divider" />
          <button
            type="button"
            role="menuitem"
            className="portal-cog-item portal-cog-item-signout"
            onClick={handleSignOut}
          >
            <span className="portal-cog-item-icon" aria-hidden="true">
              &#x21A9;
            </span>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function CogIcon() {
  // Same SVG path as nexanomics-nav.js icons.cog — keeps visual parity with
  // the canonical component.
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
